
import React, { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { lightTheme, darkTheme } from '../theme/theme'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  toggleTheme: () => void
  theme: Theme
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeContextProvider')
  }
  return context
}

interface ThemeContextProviderProps {
  children: ReactNode
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light')

  // Load theme preference from localStorage on mount
  useEffect(() => {
    try {
      const savedMode = localStorage.getItem('blackbook-theme-mode') as ThemeMode
      if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
        setMode(savedMode)
      } else {
        // Fall back to system preference if no saved preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        const systemMode: ThemeMode = prefersDark ? 'dark' : 'light'
        setMode(systemMode)
      }
    } catch (error) {
      console.warn('Failed to load theme preference:', error)
      // Fall back to system preference on error
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setMode(prefersDark ? 'dark' : 'light')
    }
  }, [])

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('blackbook-theme-mode', mode)
    } catch (error) {
      console.warn('Failed to save theme preference:', error)
    }
  }, [mode])

  const toggleTheme = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light')
  }

  const theme = mode === 'light' ? lightTheme : darkTheme

  const value = {
    mode,
    toggleTheme,
    theme,
  }

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}